import { Item } from '@react-stately/collections'
import { CollectionElement } from '@react-types/shared'
import { MoreThick } from 'almanac-ui/icons'
import { useModals } from 'almanac-ui/Modal'
import { UserService } from 'api/clients/UserService'
import { UserAppPreferencesService } from 'api/services/UserAppPreferencesService'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { LeaveWorkspaceModal } from 'components/LeaveWorkspaceModal/LeaveWorkspaceModal'
import { Menu } from 'components/Menu/Menu'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { useAlmanacWorkspace } from 'hooks/useAlmanacWorkspace'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { observer } from 'mobx-react-lite'
import { CORE_PATH, WORKSPACE_PATH } from 'paths'
import { Key } from 'react'
import { generatePath, useHistory } from 'react-router-dom'
import { useRootStore } from 'stores/RootStore'
import { SidebarTabs, Workspace } from 'types'
import { isAlmanacCoreWorkspace } from 'utils/workspaceUtils'
import Styles from './WorkspacesMenu.module.scss'
import { ALMANAC_CORE_WORKSPACE_ID } from 'utils/constants'
import { Placement } from '@floating-ui/core'
import { ButtonVariant } from 'blaze-ui/Button/Types'
import { useDefaultWorkspacePath } from 'utils/useDefaultWorkspacePath'

interface Props {
  workspace: Workspace
  onAction?: (key: Key) => void
  hiddenKeys?: Set<Key>
  placement?: Placement
  buttonVariant?: ButtonVariant
}

export const WorkspaceMenuItemActions = observer(
  ({ workspace, onAction, hiddenKeys, placement = 'bottom-start', buttonVariant = 'tertiary' }: Props) => {
    const { workspaceTheme } = useRootStore()
    const history = useHistory()
    const { openModal } = useModals()
    const workspaces = useWorkspaces()
    const almanacCoreWorkspace = useAlmanacWorkspace()

    const { defaultWorkspacePath } = useDefaultWorkspacePath()

    const onLeave = () => {
      const nextWorkspace =
        workspaces.find((w) => workspace?.id !== w.id && !isAlmanacCoreWorkspace(w.id)) ?? almanacCoreWorkspace
      const redirectPath = isAlmanacCoreWorkspace(nextWorkspace.id)
        ? CORE_PATH
        : generatePath(WORKSPACE_PATH, { workspaceId: nextWorkspace.id })
      UserAppPreferencesService.updateActiveTab(SidebarTabs.Organizational)
      UserService.setCurrentWorkspaceId(workspace.id)
      history.push(redirectPath)
    }

    const handleAction = (key: React.Key) => {
      switch (key) {
        case 'leave':
          openModal(LeaveWorkspaceModal, {
            workspace,
            onLeave,
          })
          break
        case 'rename':
          UserService.setCurrentWorkspaceId(workspace.id)
          const path = defaultWorkspacePath({
            id: workspace.id,
            homepageDocSlug: workspace.homepage_doc_slug,
            search: 'settings=general',
          })
          history.push(path)
          break
        default:
      }
      onAction?.(key)
    }

    const showLeave = workspaces.filter((w) => w.id !== ALMANAC_CORE_WORKSPACE_ID).length > 1
    const showRename =
      workspace?.admin && !hiddenKeys?.has('rename') && workspace?.is_virtual_marketer_onboarding_completed

    if (!showLeave && !showRename) return null

    return (
      <MenuTrigger
        placement={placement}
        popoverClassName={Styles.workspacePopover}
        __UNSAFE_popoverStyle={workspaceTheme.palette}
      >
        <IconButton size="sm" icon={MoreThick} aria-label="Open workspace actions menu" variant={buttonVariant} />
        <Menu onAction={handleAction}>
          {
            [
              showRename && <Item key="rename">Rename Workspace</Item>,
              showLeave && <Item key="leave">Leave Workspace</Item>,
            ].filter(Boolean) as CollectionElement<HTMLElement>[]
          }
        </Menu>
      </MenuTrigger>
    )
  },
)
